<template lang="html">
	<tr :class="rowClasses">
		<!-- Select All -->
		<table-cell :has-checkbox="true">
			<input-checkbox
				v-if="showCheckbox"
				:id="'select-' + campaign.id"
				:checked="-1 !== $store.state.campaigns.bulk.indexOf(campaign.id)"
				:value="campaign.id"
				:name="'select-' + campaign.id"
				class="campaign-select-wrapper"
				@input="changeCheckbox"
			/>
		</table-cell>
		<!-- Campaign Name & Type -->
		<table-cell class="campaign-table-name" :class="{ 'campaign-table-name-has-indicators': hasIndicator }">
			<div v-if="hasIndicator" class="campaign-table-indicators">
				<common-tooltip
					v-if="onFire"
					class="campaign-on-fire campaign-table-icon"
					placement="bottom"
					tooltip="This campaign is performing at an exceptional rate!"
					icon="svg-fire"
				/>
				<common-tooltip
					v-if="isSplit"
					class="campaign-has-split campaign-table-icon"
					placement="bottom"
					tooltip="This campaign has split tests!"
					icon="svg-split"
				/>
			</div>
			<div class="campaign-table-row-title">
				<a :href="builderLink" :title="campaign.name" class="main-campaign-link">
					{{ campaign.name }}
				</a>
				<a :href="builderLink" :title="campaignType" class="no-link-style">
					{{ campaignType }}
				</a>
			</div>
			<div class="row-actions">
				<span> <a :href="builderLink" :title="`Edit Design for “${campaign.name}”`"> Edit Design</a> | </span>
				<span v-if="!isSplit">
					<router-link :to="linkTo" :title="`Edit Output Settings for “${campaign.name}”`">
						Output Settings</router-link
					>
					|
				</span>
				<span>
					<a href="#" :title="`Change Status for “${campaign.name}”`" @click.prevent="statusOpened = true">
						Change Status</a
					>
				</span>
			</div>
		</table-cell>
		<!-- Campaign Stats -->
		<table-cell class="campaign-table-stats">
			<ul class="omapi-multi-column__meta omapi-multi-column__vert">
				<li>
					<span class="omapi-multi-column__meta-attr">Visitors: </span
					><span class="omapi-multi-column__meta-value">{{ impressions }}</span>
				</li>
				<li>
					<span class="omapi-multi-column__meta-attr">Conversions: </span
					><span class="omapi-multi-column__meta-value">{{ conversions }}</span>
				</li>
				<li>
					<span class="omapi-multi-column__meta-attr">CR%: </span
					><span class="omapi-multi-column__meta-value">{{ rate }}%</span>
				</li>
			</ul>
		</table-cell>
		<!-- A/B Tests -->
		<table-cell class="campaign-table-ab">
			<common-tooltip v-if="hasSplits" tag-name="div" :tooltip="abTooltip" :icon="false">
				<core-button :link-style="true" @click="toggleAbTests">{{ abTestAction }}</core-button>
			</common-tooltip>
			<common-tooltip v-if="!isSplit && canEdit" tooltip="Create a new A/B test" :icon="false">
				<core-button :link-style="true" @click="maybeShowSplitModal">Create Test</core-button>
			</common-tooltip>
			<common-tooltip v-if="splitsOpen" :tooltip="abTooltip" :icon="false">
				<button type="button" class="split-button" @click="toggleAbTests">
					<svg-split /><span>A/B</span><svg-chevron-up />
				</button>
			</common-tooltip>
		</table-cell>
		<!-- Status -->
		<table-cell class="campaign-table-status">
			<div class="content">
				<campaigns-status-select
					:campaign-id="id"
					:status-opened="statusOpened"
					@close="statusOpened = false"
				/>
			</div>

			<div v-if="debug" style="white-space: nowrap">
				status:
				<code :style="`color: ${'active' === campaignStatus ? 'green' : 'red'}`">{{ campaignStatus }}</code>
			</div>
			<div v-if="debug" style="white-space: nowrap">
				enabled: <code :style="`color: ${settingEnabled ? 'green' : 'red'}`">{{ settingEnabled }}</code>
			</div>
		</table-cell>
		<!-- Actions -->
		<table-cell class="campaign-table-actions">
			<common-tooltip
				class="action-campaign-screenshots"
				tooltip="View Campaign Screenshots"
				:icon="false"
				:delay-on-mouse-over="0"
			>
				<core-button color="white" size="icon" @click="showModal('preview')">
					<i class="fa fa-eye" alt="Preview"></i>
				</core-button>
			</common-tooltip>
			<common-tooltip
				v-if="$store.getters.allowed('roles', 'analytics.view')"
				class="action-campaign-analytics"
				tooltip="Analytics"
				:icon="false"
				:delay-on-mouse-over="0"
			>
				<core-button color="white" size="icon" :is-link="true" :href="analyticsLink">
					<svg-analytics-bars />
				</core-button>
			</common-tooltip>
			<common-tooltip
				v-if="canEdit"
				class="action-campaign-duplicate"
				tooltip="Create Duplicate"
				:icon="false"
				:delay-on-mouse-over="0"
			>
				<core-button color="white" size="icon" @click="showModal('duplicate')">
					<i class="fa fa-clone" alt="Clone"></i>
				</core-button>
			</common-tooltip>
			<common-tooltip
				v-if="canEdit"
				class="action-trash-campaign"
				tooltip="Trash Campaign"
				:icon="false"
				:delay-on-mouse-over="0"
			>
				<core-button color="white" size="icon" :disabled="isBulk" @click="showModal('trash')">
					<i class="fa fa-trash-o" alt="Trash"></i>
				</core-button>
			</common-tooltip>
			<!-- icon="span" is necessary to not throw popper errors. -->
			<common-tooltip
				v-if="!isSplit"
				class="action-more"
				tooltip="More Actions"
				icon="span"
				:delay-on-mouse-over="0"
			>
				<core-dropdown-button class="campaign-table-additional-actions" align="right" :width="167">
					<template #list>
						<li v-if="!isBulk && canEdit">
							<a href="#" @click.prevent="showModal('archive')">Archive</a>
						</li>
						<li v-if="!isSplit">
							<router-link :to="linkTo" :title="`Output Settings for: ${campaign.name}`">
								Output Settings
							</router-link>
						</li>
						<li><a href="#" @click.prevent="maybeShowMonsterLink">Shareable Link</a></li>
					</template>
				</core-dropdown-button>
			</common-tooltip>
		</table-cell>
	</tr>
</template>

<script lang="js">
import { campaign } from '@/campaigns/mixins/campaign'
import { pluralize } from 'optinmonster-js-library/src/utils/strings'
import { debug } from '@/utils'
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapState, mapMutations } = createNamespacedHelpers('campaigns')

/**
 * Campaign Table Row
 * @displayName Campaign Table Row
 * @since 2.0.0
 */
export default  {
	mixins : [ campaign ],
	props  : {
		/**
		 * Campaign Id
		 */
		campaignId : {
			type : String,
			default: '',
		},
		/**
		 * Whether row is visible.
		 *
		 * @type {Boolean}
		 */
		rowVisible : Boolean,
		/**
		 * Whether campaign's split tests are open.
		 *
		 * @type {Boolean}
		 */
		splitsOpen : Boolean,
	},
	data () {
		return {
			statusOpened : false,
		}
	},
	computed : {
		...mapGetters([ 'getCampaign' ]),
		...mapState([ 'bulk' ]),
		/**
		 * Get the campaign object.
		 * @returns {Object} The campaign object.
		 */
		campaign () {
			return this.getCampaign(this.campaignId)
		},
		/**
		 * Whether we have any indicator icons.
		 *
		 * @since  2.0.0
		 *
		 * @returns {boolean} Whether we have indicator icons.
		 */
		hasIndicator () {
			return this.onFire || this.isSplit
		},
		/**
		 * Get the campaign or parent campaign object.
		 * @returns {Object} The campaign or parent campaign object.
		 */
		campaignOrParent () {
			return this.isSplit ? this.getCampaign(this.campaign.parent_id) : this.campaign
		},
		/**
		 * Determine if multiple campaigns have been selected.
		 * @returns {boolean} have multiple campaigns been selected?
		 */
		isBulk () {
			return 0 < this.bulk.length
		},
		/**
		 * Get the appropriate action for the A/B Tests column.
		 * @returns {string} The HTML for the action.
		 */
		abTestAction () {
			const count = this.splits.length
			return `${count} ${pluralize('Test', count)}`
		},
		/**
		 * Get the text for the A/B test tooltip.
		 * @returns {string} The tooltip text.
		 */
		abTooltip () {
			return this.splitsOpen
				? 'Click to close A/B tests'
				: 'Click to reveal A/B tests'
		},
		/**
		 * Get the path to edit the settings for this campaign.
		 *
		 * Note: Keeping this atm, likely will be removed.
		 *
		 * @returns {string} The URI
		 */
		linkTo () {
			return { name: 'campaign-edit', params: { campaignId: this.campaignId || 0 } }
		},
		/**
		 * Generate classes for this campaign row.
		 * @returns {string} The css classes for the element.
		 */
		rowClasses () {
			let classes = this.isSplit
				? `child-of-${this.campaign.parent_id} child-row`
				: ''

			if (!this.rowVisible) {
				classes += ' hidden'
			}

			return classes
		},
		debug () {
			return debug.enabled('status')
		},
	},
	methods : {
		...mapMutations([ 'addToBulk', 'removeFromBulk' ]),
		/**
		 * Toggle the A/B tests for a campaign.
		 * @returns {void}
		 */
		toggleAbTests () {
			this.$emit('toggleAbTests', this.campaign.id)
		},
		/**
		 * Take the appropriate action when a checkbox is checked/unchecked.
		 * @param {boolean} value is the checkbox checked
		 * @param {string} checked the campaign id
		 * @returns {void}
		 */
		changeCheckbox (value, checked) {
			if (checked) {
				this.addToBulk(this.campaign.id)
			} else {
				this.removeFromBulk(this.campaign.id)
			}
		},
		/**
		 * Determine if user can edit this campaign (or its parent).
		 * @returns {boolean} Whether user can edit.
		 */
		canEdit () {
			return this.$store.getters.allowed('roles', 'campaign.edit', this.campaignOrParent)
		},
		/**
		 * Determine if a the select checkbox should be shown.
		 * @returns {boolean} Should we show the checkbox?
		 */
		showCheckbox () {
			return this.canEdit && !this.isSplit
		},
		/**
		 * Opens the split-test modal, OR the pro-features modal.
		 * @returns {void}
		 */
		maybeShowSplitModal () {
			return this.$store.getters.hasFeature('ab-testing')
				? this.showModal('split')
				: this.$bus.$emit('toggleProModal', 'CampaignSplitAction', [ 'split-test' ])
		},
		/**
		 * Opens the monster-link click to copy modal, OR the pro-features modal.
		 * @returns {void}
		 */
		maybeShowMonsterLink () {
			return this.$store.getters.hasFeature('monsterlinks')
				? this.showModal('monster-link')
				: this.$bus.$emit('toggleProModal', 'CampaignMonsterLink', [ 'monster-link' ])
		},
	},
};
</script>

<style scoped lang="scss">
@import '../../assets/scss/variables';

.row-bg-odd {
	background-color: #f9f9f9;
}

.omapi-table {
	.campaign-table-name {
		.no-link-style {
			color: $font_secondary-color;
			font-weight: initial;
		}
	}

	.campaign-table-ab {
		position: relative;

		.split-button {
			align-items: center;
			background-color: $color_white-background;
			border: 1px solid $color_grey-border;
			border-radius: 2px;
			bottom: -10px;
			color: $font_secondary-color;
			display: flex;
			font-family: 'Proxima Nova', sans-serif;
			font-size: 0.75rem;
			font-weight: 600;
			height: 20px;
			justify-content: center;
			left: 15px;
			position: absolute;
			z-index: 99;

			&:hover {
				background-color: $color_blue-primary;
				border-color: $color_blue-primary;
				color: $color_white;
				cursor: pointer;
			}

			&.hidden {
				display: none;
			}

			span {
				margin-right: 2px;
			}

			svg {
				height: 12px;
				width: auto;
			}
		}
	}

	.campaign-table-status {
		.content {
			display: flex;
		}

		.scheduled-button {
			background-color: transparent;
			border: none;
			margin-left: 2px;
			padding: 0;
		}
	}

	.campaign-table-actions {
		.omapi-button {
			display: inline-flex;
			justify-content: center;
			align-items: center;
			margin-right: 0;
			height: 36px;
			width: 36px;
			box-sizing: border-box;
			padding: 0;
		}
		.action-trash-campaign,
		.action-more {
			.omapi-button {
				width: 33px;
			}
		}
	}
}

.omapi-screen .campaign-table-additional-actions {
	.omapi-button {
		margin-right: 0;
	}
}
.omapi-table {
	th {
		&.campaign-table-actions {
			width: 180px;
		}
	}
}
</style>
